import * as React from 'react';
import * as R from 'ramda';
import { Form, Text, Select, FormValues, FormErrors } from 'react-form';
import { BaseFormState, BaseFormProps } from '../../interfaces';
import {Organization, UserTypeahead, UserTeam, SurveyCompleteUser, User, UserPermissions} from '../../models';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import {
    TypeaheadService,
    ValidationService,
    NotificationService,
    SurveyInstanceDataService,
    ReportDataService,
    UserDataService, Auth,
    StyleDataService,
    CharacteristicGroupDataService,
    SurveyReportTypeDataService,
} from '../../services';
import { PdfFoundations } from '../reports/individual/pdf_foundations';
import { exportToPDFBlob } from '../../utilities/Pdf';
import { InputGroup } from 'react-bootstrap';
import { UserStore, FilterStore } from '../../stores';
import { Roles } from '../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

import './TeamForm.css';
import {LoadingIndicator} from "../loadingIndicator/LoadingIndicator";
import * as moment from "moment";
import Datepicker from "react-datepicker";
import {Prompt} from 'react-router';
import {hasPermission, togglePermission} from "../../utilities/User";
// import {BatchFunctions} from "../../constants/Permissions";

interface FormState extends BaseFormState {
    selectedOrganizationId?: number;
    selectedTeamMembers?: UserTypeahead[];
    selectedAssociateTeamMembers?: UserTypeahead[];
    selectedUserIdsForBatch: number[];
    selectedUserIdsForTypesReport: number[];
    selectedUserIdsForBriefIndividualReport: number[];
    selectedUserIdsForFoundationsReport: number[];
    inviteSentByUserId: {};
    surveyCompletedByUserId: {};
    inviteExpiration: moment.Moment;
    confirmedTeamMembers: UserTeam[];
    confirmedAssociateTeamMembers: SurveyCompleteUser[];
    teamMemberTypeahead: {
        allowNew: boolean;
        isLoading: boolean;
        multiple: boolean;
        options: any[];
    };
    teamAssociateMemberTypeahead: {
        allowNew: boolean;
        isLoading: boolean;
        multiple: boolean;
        options: any[];
    };
    canSubmit: boolean;
    formErrors: FormErrors;
    isLoading: boolean;
    saveNeeded: boolean;
    showModal: boolean;
    fullUsersById: Map<number, User>;
    unchangedFullUsersById: Map<number, string>;
    failedUserIds: number[];
}

interface TeamFormProps extends BaseFormProps {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    organizations: Organization[];
    notificationService: NotificationService;
}

@inject('UserStore', 'FilterStore') @observer
export class TeamForm extends React.Component<TeamFormProps, FormState> {
    private surveyInstanceDataService = new SurveyInstanceDataService(this.props.notificationService);
    private userDataService = new UserDataService(this.props.notificationService);
    private reportDataService = new ReportDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private surveyReportTypeDataService = new SurveyReportTypeDataService(this.props.notificationService);
    private vs = new ValidationService();
    private typeaheadService = new TypeaheadService(this.props.notificationService);
    private teamMemberTypeahead: any;
    private teamAssociateMemberTypeahead: any;

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get canBatch() {
        return this.props.UserStore!.checkUserPermission(UserPermissions.BatchFunctions);
    }

    constructor(props: TeamFormProps) {
        super(props);

        this.state = {
            confirmedTeamMembers: [],
            confirmedAssociateTeamMembers: [],
            selectedUserIdsForBatch: [],
            selectedUserIdsForTypesReport: [],
            selectedUserIdsForBriefIndividualReport: [],
            selectedUserIdsForFoundationsReport: [],
            inviteSentByUserId: {},
            surveyCompletedByUserId: {},
            inviteExpiration: moment().add(7, 'd'),
            teamMemberTypeahead: {
                allowNew: false,
                isLoading: false,
                multiple: true,
                options: [],
            },
            teamAssociateMemberTypeahead: {
                allowNew: false,
                isLoading: false,
                multiple: true,
                options: [],
            },
            canSubmit: false,
            formErrors: {},
            isLoading: false,
            saveNeeded: false,
            showModal: false,
            fullUsersById: new Map<number, User>(),
            unchangedFullUsersById: new Map<number, string>(),
            failedUserIds: [],
        };

        this.handleSearch = this.handleSearch.bind(this);
        this.handleAssociateSearch = this.handleAssociateSearch.bind(this);
        this.formDidUpdate = this.formDidUpdate.bind(this);
        this.addUser = this.addUser.bind(this);
        this.removeUserBinder = this.removeUserBinder.bind(this);
        this.addAssociateUser = this.addAssociateUser.bind(this);
        this.removeAssociateUserBinder = this.removeAssociateUserBinder.bind(this);
        this.typeaheadOnChange = this.typeaheadOnChange.bind(this);
        this.typeaheadAssociateOnChange = this.typeaheadAssociateOnChange.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleIsManagerChangeBinder = this.handleIsManagerChangeBinder.bind(this);
        this.handleIsMemberChangeBinder = this.handleIsMemberChangeBinder.bind(this);

        this.validateErrors = this.validateErrors.bind(this);
        this.validateSuccess = this.validateSuccess.bind(this);
    }

    private handleSearch(filter: string) {
        const { selectedOrganizationId, teamMemberTypeahead, confirmedTeamMembers } = this.state;
        if (selectedOrganizationId) {
            this.setState({ teamMemberTypeahead: Object.assign({}, teamMemberTypeahead, { isLoading: true }) });

            this.typeaheadService.getUserTypeaheadsByOrganization(selectedOrganizationId, filter)
                .then((response: UserTypeahead[]) => {
                    const potentialMembers = response.filter((user) => !confirmedTeamMembers.some(x => x.userID === user.id));
                    this.setState({
                        teamMemberTypeahead: Object.assign(
                            {},
                            teamMemberTypeahead,
                            {
                                isLoading: false,
                                options: potentialMembers
                            }
                        )
                    });
                });
        }
    }

    private handleAssociateSearch(filter: string) {
        const { selectedOrganizationId, teamAssociateMemberTypeahead, confirmedAssociateTeamMembers } = this.state;
        if (selectedOrganizationId) {
            this.setState({ teamAssociateMemberTypeahead: Object.assign({}, teamAssociateMemberTypeahead, { isLoading: true }) });

            this.typeaheadService.getUserTypeaheadsByRole(selectedOrganizationId, filter)
                .then((response: UserTypeahead[]) => {
                    const potentialMembers = response.filter((user) => !confirmedAssociateTeamMembers.some(x => x.userID === user.id));
                    this.setState({
                        teamAssociateMemberTypeahead: Object.assign(
                            {},
                            teamAssociateMemberTypeahead,
                            {
                                isLoading: false,
                                options: potentialMembers
                            }
                        )
                    });
                });
        }
    }

    componentDidMount() {
        if (this.props.defaultValues) {
            // Keep every UserTeam row, including manager-only rows (isMember false). Filtering them out here
            // hid managers from the table and dropped them from the payload on the next save.
            const members: UserTeam[] = this.props.defaultValues.members || [];

            this.setState({
                selectedOrganizationId: this.props.defaultValues.organizationId,
                confirmedTeamMembers: members,
                confirmedAssociateTeamMembers: this.props.defaultValues.surveyCompleteUsers
            });

            const fullUsersById = new Map<number, User>();
            const unchangedFullUsersById = new Map<number, string>();
            const failedUserIds: number[] = [];

            // Load each user independently so a single failure does not discard the rest.
            const userPromises = members
                .filter((userTeam: UserTeam) => !!userTeam.userID)
                .map((userTeam: UserTeam) => this.userDataService.get(userTeam.userID!)
                    .then((user: User) => {
                        fullUsersById.set(user.id, user);
                        unchangedFullUsersById.set(user.id, JSON.stringify(user));
                    })
                    .catch((error: any) => {
                        console.error(`Error loading team member data for user ${userTeam.userID}:`, error);
                        failedUserIds.push(userTeam.userID!);
                    }));

            Promise.all(userPromises).then(() => {
                if (failedUserIds.length > 0) {
                    this.props.notificationService.addError('Error loading some team member data');
                }
                this.setState({
                    fullUsersById: fullUsersById,
                    unchangedFullUsersById: unchangedFullUsersById,
                    failedUserIds: failedUserIds
                });
            });
        }
    }

    private formDidUpdate(formState: any) {
        if (formState.values) {
            if (formState.values.organizationId) {
                this.setState({ selectedOrganizationId: formState.values.organizationId });
            }
        }
    }

    private typeaheadOnChange(selected: Array<Object | String>) {
        this.setState({ selectedTeamMembers: selected as UserTypeahead[] });
    }

    private typeaheadAssociateOnChange(selected: Array<Object | String>) {
        this.setState({ selectedAssociateTeamMembers: selected as UserTypeahead[] });
    }

    componentDidUpdate() {
        for (const userteam of this.state.confirmedTeamMembers) {
            // Manager-only rows are not surveyed, so don't look up invites/completions for them.
            if (!this.isBatchable(userteam)) {
                continue;
            }
            if (userteam.userID && this.state.inviteSentByUserId[userteam.userID] === undefined) {
                this.state.inviteSentByUserId[userteam.userID] = null;
                this.userHasInvite(userteam.userID!).then((value) => {
                    this.state.inviteSentByUserId[userteam.userID!] = value;
                    this.forceUpdate();
                });
            }
            if (userteam.userID && this.state.surveyCompletedByUserId[userteam.userID] === undefined) {
                this.state.surveyCompletedByUserId[userteam.userID] = null;
                this.getLatestCompletedInstanceForUserId(userteam.userID).then((instance) => {
                    if (instance === null) {
                        this.state.surveyCompletedByUserId[userteam.userID!] = false;
                    } else {
                        this.state.surveyCompletedByUserId[userteam.userID!] = instance;
                    }
                    this.forceUpdate();
                });
            }
        }
    }

    private addUser(e: any) {
        e.preventDefault();
        const { selectedTeamMembers, confirmedTeamMembers } = this.state;

        if (selectedTeamMembers && selectedTeamMembers.length > 0) {
            const newMembers = selectedTeamMembers.filter(user => {
                return !confirmedTeamMembers.some(x => x.userID === user.id);
            });

            const userTeams: UserTeam[] = newMembers.map(user => {
                return {
                    user: user,
                    userID: user.id,
                    isManager: false,
                    isMember: true
                };
            });

            newMembers.forEach(user => {
                this.userDataService.get(user.id).then((fullUser: User) => {
                    const { fullUsersById, unchangedFullUsersById } = this.state;
                    fullUsersById.set(fullUser.id, fullUser);
                    unchangedFullUsersById.set(fullUser.id, JSON.stringify(fullUser));
                    this.setState({ fullUsersById: fullUsersById, unchangedFullUsersById: unchangedFullUsersById });
                });
            });

            this.setState({
                confirmedTeamMembers: [...confirmedTeamMembers, ...userTeams],
                selectedTeamMembers: []
            });

            this.teamMemberTypeahead.getInstance().clear();
        }
    }

    private removeUser(userId: number) {
        const { confirmedTeamMembers } = this.state;

        this.setState({ confirmedTeamMembers: confirmedTeamMembers.filter((x: UserTeam) => x.userID !== userId) });
    }

    private removeUserBinder(id: number): ((event: React.MouseEvent<HTMLElement>) => void) {

        return this.removeUser.bind(this, id);
    }

    private addAssociateUser(e: any) {
        e.preventDefault();
        const { selectedAssociateTeamMembers, confirmedAssociateTeamMembers } = this.state;

        if (selectedAssociateTeamMembers && selectedAssociateTeamMembers.length > 0) {
            const newMembers = selectedAssociateTeamMembers.filter(user => {
                return !confirmedAssociateTeamMembers.some(x => x.userID === user.id);
            });

            const userTeams: SurveyCompleteUser[] = newMembers.map(user => {
                var u = new User();
                u.id = user.id;
                u.firstName = user.firstName;
                u.lastName = user.lastName;
                return {
                    id: 0,
                    userID: user.id,
                    user: u
                };
            });

            this.setState({
                confirmedAssociateTeamMembers: [...confirmedAssociateTeamMembers, ...userTeams],
                selectedAssociateTeamMembers: []
            });

            this.teamAssociateMemberTypeahead.getInstance().clear();
        }
    }

    private removeAssociateUser(userId: number) {
        const { confirmedAssociateTeamMembers } = this.state;

        this.setState({ confirmedAssociateTeamMembers: confirmedAssociateTeamMembers.filter((x: SurveyCompleteUser) => x.userID !== userId) });
    }

    private removeAssociateUserBinder(id: number): ((event: React.MouseEvent<HTMLElement>) => void) {

        return this.removeAssociateUser.bind(this, id);
    }

    private handleIsManagerChange(userId: number) {
        const { confirmedTeamMembers } = this.state;

        const teamMembers = confirmedTeamMembers.map((x: UserTeam) => {
            if (x.userID !== userId) {
                return x;
            }

            x.isManager = !x.isManager;
            return x;
        });

        this.setState({ confirmedTeamMembers: teamMembers, saveNeeded: true });
    }
    
    private handleFoundationsChange(userId: number) {
        const { confirmedTeamMembers } = this.state;
        
        if (!this.state.fullUsersById.has(userId)) return;

        const teamMembers = confirmedTeamMembers.map((x: UserTeam) => {
            if (x.userID !== userId) {
                return x;
            }

            const fullUser = this.state.fullUsersById.get(userId)!;
            fullUser.userPermissions = togglePermission(fullUser.userPermissions, UserPermissions.Foundations);
            
            return x;
        });

        this.setState({ confirmedTeamMembers: teamMembers, saveNeeded: true });
    }

    private handleIsManagerChangeBinder(id: number): any {
        return this.handleIsManagerChange.bind(this, id);
    }

    private async handleSelectUser(id: number) {
        let batch = this.state.selectedUserIdsForBatch;
        const index = batch.findIndex((val) => { return val === id; });
        if (index >= 0) {
            batch.splice(index, 1);
        } else {
            batch.push(id);
        }
        await this.setState({...this.state, selectedUserIdsForBatch: batch});
    }
    private async reportTypeDropdownChanged(e: any, userId: number) {
        e.preventDefault();
        const selectedValue = e.target.value;
        let briefReportUsers = [...this.state.selectedUserIdsForBriefIndividualReport];
        let foundationsReportUsers = [...this.state.selectedUserIdsForFoundationsReport];
        const briefIndex = briefReportUsers.findIndex((val) => { return val === userId; });
        const foundationsIndex = foundationsReportUsers.findIndex((val) => { return val === userId; });

        if (selectedValue === 'brief') {
            if (briefIndex < 0) {
                briefReportUsers.push(userId);
            }
            if (foundationsIndex >= 0) {
                foundationsReportUsers.splice(foundationsIndex, 1);
            }
        } else if (selectedValue === 'foundations') {
            if (foundationsIndex < 0) {
                foundationsReportUsers.push(userId);
            }
            if (briefIndex >= 0) {
                briefReportUsers.splice(briefIndex, 1);
            }
        } else {
            if (briefIndex >= 0) {
                briefReportUsers.splice(briefIndex, 1);
            }
            if (foundationsIndex >= 0) {
                foundationsReportUsers.splice(foundationsIndex, 1);
            }
        }
        await this.setState({...this.state, selectedUserIdsForBriefIndividualReport: briefReportUsers, selectedUserIdsForFoundationsReport: foundationsReportUsers});
    }
    private async toggleAllForExportReportType(e: any) {
        e.preventDefault();
        const userCount = this.state.selectedUserIdsForBatch.length;
        const briefCount = this.state.selectedUserIdsForBriefIndividualReport.length;
        const foundationsCount = this.state.selectedUserIdsForFoundationsReport.length;

        let allIds: number[] = [];
        for(let id of this.state.selectedUserIdsForBatch) allIds.push(id);

        // Cycle: Full -> Brief -> Foundations -> Full for all selected users.
        if (briefCount !== userCount && foundationsCount !== userCount) {
            // Currently Full (or mixed) -> switch all to Brief
            this.setState({selectedUserIdsForBriefIndividualReport: allIds, selectedUserIdsForFoundationsReport: []});
        } else if (briefCount === userCount) {
            // Currently Brief -> switch all to Foundations
            this.setState({selectedUserIdsForBriefIndividualReport: [], selectedUserIdsForFoundationsReport: allIds});
        } else {
            // Currently Foundations -> switch all to Full
            this.setState({selectedUserIdsForBriefIndividualReport: [], selectedUserIdsForFoundationsReport: []});
        }
    }
    private async toggleAllForPersonalityTypes(e: any) {
        e.preventDefault();
        const selectedCount = this.state.selectedUserIdsForTypesReport.length;
        const userCount = this.state.selectedUserIdsForBatch.length;
        if (selectedCount !== userCount) {
            let allIds = [];
            for(let id of this.state.selectedUserIdsForBatch) allIds.push(id);
            this.setState({selectedUserIdsForTypesReport: allIds});
        }
        else {
            this.setState({selectedUserIdsForTypesReport: []});
        }
    }
    private async handleToggleTypesReport(id: number) {
        let batch = this.state.selectedUserIdsForTypesReport;
        const index = batch.findIndex((val) => { return val === id; });
        if (index >= 0) {
            batch.splice(index, 1);
        } else {
            batch.push(id);
        }
        await this.setState({...this.state, selectedUserIdsForTypesReport: batch});
    }
    // Batch actions (invites, logins, report PDFs) only make sense for actual team members,
    // so manager-only rows are excluded from the batch even though they are listed in the table.
    private isBatchable(userteam: UserTeam) {
        return !!userteam && !!userteam.isMember;
    }

    private async toggleAllForBatch(e: any) {
        e.preventDefault();
        let confirmed = this.state.confirmedTeamMembers.filter(
            (m) => { return m !== null && m !== undefined && this.isBatchable(m); });
        let batch = this.state.selectedUserIdsForBatch;
        if (batch.length === confirmed.length) {
            batch.splice(0);
        } else {
            batch = confirmed.map((m) => { return m.userID || -1; });
        }
        await this.setState({...this.state, selectedUserIdsForBatch: batch});
    }
    createLoginsForBatch = async (e: any) => {
        e.preventDefault();
        if (this.state.selectedUserIdsForBatch.length < 1) return;
        if (!window.confirm(`Are you sure you want to create logins for ${this.state.selectedUserIdsForBatch.length} user(s)?`)) {
            return;
        }
        this.setState({isLoading: true});
        for (const userId of this.state.selectedUserIdsForBatch) {
            try {
                await this.userDataService.createPersonalAccount(userId);
                this.props.notificationService.addNotification(`Created login for ${this.getUserName(userId)}`);
            } catch (e) {
                console.log(`Exception while creating logins for batch: `);
                console.log(e);
                this.props.notificationService.addError(`Failed to create login for ${this.getUserName(userId)}`);
            }
        }
        this.setState({isLoading: false});
    }
    inviteBatch = async (e: any) => {
        e.preventDefault();
        if (this.state.selectedUserIdsForBatch.length < 1) return;
        if (!window.confirm(`Are you sure you want to invite ${this.state.selectedUserIdsForBatch.length} user(s)?`)) {
            return;
        }
        this.setState({isLoading: true});
        for (const userId of this.state.selectedUserIdsForBatch) {
            try {
                this.state.inviteSentByUserId[userId] = undefined;
                this.forceUpdate();
                let expirationDate = this.state.inviteExpiration.toDate();
                let values = {
                    userID: userId,
                    expirationDate: expirationDate.toUTCString(),
                    surveyVersionID: this.props.FilterStore!.surveyVersion!.id,
                };
                this.surveyInstanceDataService.create(values as any).then(() => {
                    this.state.inviteSentByUserId[userId] = true;
                    this.forceUpdate();
                });
            } catch (e) {
                console.log(`Exception while creating batch survey invites: `);
                console.log(e);
            }
        }
        this.setState({isLoading: false});
    }
    openBatchModal = (e: any) => {
        e.preventDefault();
        this.setState({...this.state, selectedUserIdsForTypesReport:[], selectedUserIdsForBriefIndividualReport:[], selectedUserIdsForFoundationsReport:[], showModal: true});
    }
    exportBatch = async (e: any) => {
        e.preventDefault();

        if (this.state.selectedUserIdsForBatch.length < 1) return;

        const reportTypeId = this.surveyReportType ? this.surveyReportType.id : undefined;

        this.props.notificationService.addNotification(`Individual report email batch started`);
        this.setState({isLoading:true});

        try {
            let promises = [];

            for(const userId of this.state.selectedUserIdsForBriefIndividualReport) {
                const latestSurveyInstance = await this.getLatestCompletedInstanceForUserId(userId);
                if (latestSurveyInstance === null) continue;
                const includeTypes = this.state.selectedUserIdsForTypesReport.findIndex(uid => uid === userId) >= 0;
                promises.push(this.reportDataService.createEmailRequestForBriefIndividualReport(
                    latestSurveyInstance.id, reportTypeId!, includeTypes
                ));
            }
            for(const userId of this.state.selectedUserIdsForFoundationsReport) {
                const latestSurveyInstance = await this.getLatestCompletedInstanceForUserId(userId);
                if (latestSurveyInstance === null) continue;
                promises.push(this.generateAndSendFoundationsReport(latestSurveyInstance.id));
            }
            for(const userId of this.state.selectedUserIdsForBatch) {
                if (this.state.selectedUserIdsForBriefIndividualReport.findIndex(uid => uid === userId) < 0 &&
                    this.state.selectedUserIdsForFoundationsReport.findIndex(uid => uid === userId) < 0) {
                    const latestSurveyInstance = await this.getLatestCompletedInstanceForUserId(userId);
                    if (latestSurveyInstance === null) continue;
                    const includeTypes = this.state.selectedUserIdsForTypesReport.findIndex(uid => uid === userId) >= 0;
                    promises.push(this.reportDataService.createEmailRequestForFullIndividualReport(
                        latestSurveyInstance.id, reportTypeId!, includeTypes
                    ));
                }
            }

            await Promise.all(promises);

            this.props.notificationService.addNotification(`Individual report email batch complete`);
            this.hideBatchModal();
        } catch (error) {
            console.log(error);
            this.props.notificationService.addError('Error sending individual report email batch.');
        } finally {
            this.setState({isLoading: false});
        }
    }
    // Foundations reports are generated client-side (PdfFoundations), unlike Brief/Full which the API generates
    // server-side. So for the batch flow we fetch the report data, render the PDF off-screen, and upload the
    // resulting blob to be emailed via the EmailRequests/Attachment endpoint (see shared_docs/API-CONTRACT.md).
    private generateAndSendFoundationsReport = async (surveyInstanceId: number) => {
        const reportData = await this.reportDataService.getIndividualReportDataByInstance(surveyInstanceId);
        if (!reportData) return;

        const surveyVersionId = reportData.surveyVersionId || reportData.surveyVersionID
            || (this.props.FilterStore && this.props.FilterStore.surveyVersion ? this.props.FilterStore.surveyVersion.id : undefined);

        let allStyles = reportData.allStyles;
        if (!allStyles && surveyVersionId) {
            allStyles = await this.styleDataService.getAllBySurveyVersionId(surveyVersionId);
        }

        const characteristicGroups: { [id: number]: any } = {};
        const characteristics: { [id: number]: any } = {};
        if (reportData.inclinationGroups) {
            for (const inclinationGroup of reportData.inclinationGroups) {
                const id = inclinationGroup.characteristicGroupID;
                if (id && !characteristicGroups[id]) {
                    const characteristicGroup = await this.characteristicGroupDataService.get(id);
                    characteristicGroups[id] = characteristicGroup;
                    if (characteristicGroup.characteristicGroupCharacteristics) {
                        characteristicGroup.characteristicGroupCharacteristics.forEach((c: any) => {
                            if (c.characteristic) {
                                characteristics[c.characteristic.id] = c.characteristic;
                            }
                        });
                    }
                }
            }
        }

        const surveyReportTypes = surveyVersionId ? await this.surveyReportTypeDataService.getBySurveyVersion(surveyVersionId) : [];

        const surveyVersion = (this.props.FilterStore && this.props.FilterStore.surveyVersion) || reportData.surveyVersion;
        if (!surveyVersion) return;

        const fileName = `PVA_Foundations_${reportData.name}.pdf`;

        const pdfBlob = await exportToPDFBlob(
            <PdfFoundations
                notificationService={this.props.notificationService}
                selectedUserData={reportData}
                allStyles={allStyles || []}
                surveyVersion={surveyVersion}
                characteristicGroups={characteristicGroups}
                characteristics={characteristics}
                surveyReportTypes={surveyReportTypes}
            />,
            fileName
        );

        return this.reportDataService.createEmailRequestForAttachment(pdfBlob, fileName, surveyInstanceId);
    }
    getUserName = (userId: number) => {
        for (const teamuser of this.state.confirmedTeamMembers) {
            if (teamuser.userID === userId && teamuser.user) {
                return `${teamuser.user.lastName}, ${teamuser.user.firstName}`;
            }
        }
        const fullUser = this.state.fullUsersById.get(userId);
        if (fullUser) {
            return `${fullUser.lastName}, ${fullUser.firstName}`;
        }
        return 'Nameless';
    }
    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }
    getUserTeamForUserId(userId: number) {
        const index = this.state.confirmedTeamMembers.findIndex((val) => { return val.userID === userId});
        if (index < 0) {
            return null;
        }
        return this.state.confirmedTeamMembers[index];
    }
    userIsAssignedBriefReport = (userId: number) => {
        const index = this.state.selectedUserIdsForBriefIndividualReport.findIndex((val) => { return val === userId; })
        return index >= 0;
    }
    userIsAssignedFoundationsReport = (userId: number) => {
        const index = this.state.selectedUserIdsForFoundationsReport.findIndex((val) => { return val === userId; })
        return index >= 0;
    }
    InstanceCollection = (instances:any) => {
        return {
            instances,
            sortByAge: () => {
                instances.sort((a:any,b:any) => {
                    const aDate = new Date(a.modifiedDate);
                    const bDate = new Date(b.modifiedDate);
                    return aDate === bDate ? 0 : aDate > bDate ? -1 : 1;
                })
                return this.InstanceCollection(instances);
            },
            sortByActive: () => {
                instances.sort((a:any,b:any) => {
                    return a.active === b.active ? 0 : a.active ? -1 : 1;
                });
                return this.InstanceCollection(instances);
            },
            dropDeleted: () => this.InstanceCollection(instances.filter((i:any) => {
                return !i.isDeleted;
            })),
            dropIncomplete: () => this.InstanceCollection(instances.filter((i:any) => {
                return !!i.completedDate;
            })),
        };
    }

    getSurveyInstancesForUserId = async (userId: number) => {
        return this.InstanceCollection(await this.surveyInstanceDataService.getAllForUser(userId));
    }
    userHasInvite = async (id: number) => {
        const collection = (await this.getSurveyInstancesForUserId(id))
            .dropDeleted();
        return collection.instances.length > 0;
    }
    getLatestCompletedInstanceForUserId = async (userId: number) => {
        let instances = await this.getSurveyInstancesForUserId(userId);
        const collection = (instances)
            .dropDeleted().dropIncomplete().sortByAge().sortByActive();
        if (collection.instances.length > 0) {
            return collection.instances[0];
        }
        return null;
    }

    private toggleAllFoundations(e: any) {
        e.preventDefault();
        const { confirmedTeamMembers, fullUsersById } = this.state;
        
        let anyUnchecked = false;
        confirmedTeamMembers.forEach(member => {
            if (fullUsersById.has(member.userID!)) {
                const fullUser = fullUsersById.get(member.userID!)!;
                if (!hasPermission(fullUser.userPermissions, UserPermissions.Foundations)) {
                    anyUnchecked = true;
                }
            }
        });

        const targetValue = anyUnchecked; // If any unchecked, we want to check all (true). Otherwise uncheck all (false).

        confirmedTeamMembers.forEach(member => {
            if (fullUsersById.has(member.userID!)) {
                const fullUser = fullUsersById.get(member.userID!)!;
                const currentValue = hasPermission(fullUser.userPermissions, UserPermissions.Foundations);
                if (currentValue !== targetValue) {
                    fullUser.userPermissions = togglePermission(fullUser.userPermissions, UserPermissions.Foundations);
                }
            }
        });

        this.setState({ fullUsersById, saveNeeded: true });
    }

    private handleIsMemberChange(userId: number) {
        const { confirmedTeamMembers } = this.state;

        const teamMembers = confirmedTeamMembers.map((x: UserTeam) => {
            if (x.userID !== userId) {
                return x;
            }

            x.isMember = !x.isMember;
            return x;
        });

        // A user who is no longer a member cannot take part in batch actions.
        const stillAMember = teamMembers.some((x: UserTeam) => x.userID === userId && !!x.isMember);
        const selectedUserIdsForBatch = stillAMember
            ? this.state.selectedUserIdsForBatch
            : this.state.selectedUserIdsForBatch.filter((id: number) => id !== userId);

        this.setState({
            confirmedTeamMembers: teamMembers,
            selectedUserIdsForBatch: selectedUserIdsForBatch,
            saveNeeded: true
        });
    }

    private handleIsMemberChangeBinder(id: number): any {
        return this.handleIsMemberChange.bind(this, id);
    }

    private async handleFormSubmit(values: any) {
        this.setState({isLoading: true});
        
        const savePromises: Promise<any>[] = [];
        this.state.fullUsersById.forEach((user, id) => {
            const unchangedUserJson = this.state.unchangedFullUsersById.get(id);
            if (unchangedUserJson !== JSON.stringify(user)) {
                savePromises.push(this.userDataService.update(user));
            }
        });

        try {
            await Promise.all(savePromises);
            
            const unchangedFullUsersById = new Map<number, string>();
            this.state.fullUsersById.forEach((user, id) => {
                unchangedFullUsersById.set(id, JSON.stringify(user));
            });

            this.setState({saveNeeded: false, isLoading: false, unchangedFullUsersById: unchangedFullUsersById});
            values.members = this.state.confirmedTeamMembers;
            values.surveyCompleteUsers = this.state.confirmedAssociateTeamMembers;

            this.props.submit(values);
        } catch (e) {
            this.setState({isLoading: false});
            this.props.notificationService.addError("Error saving user permissions.");
        }
    }

    // Validation methods
    validateErrors(values: FormValues) {
        const { name, organizationId } = values;
        let formErrors: any = {};

        if (name || name === '') {
            if (!this.vs.required(name)) {
                formErrors.name = 'Name is required.';
            } else if (!this.vs.cannotBeEmpty(name!)) {
                formErrors.name = 'Name cannot be blank.';
            }
        }

        if (organizationId) {
            if (!this.vs.required(organizationId)) {
                formErrors.organizationId = 'Organization is required.';
            }
        }

        return formErrors;
    }

    validateSuccess(values: FormValues, errors: FormErrors) {
        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && this.vs.validateRequiredFieldsExist(['name'], values)) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }
    // end of validation stuff

    private userTeamFilter(user1: UserTeam, user2: UserTeam) {
        if (user1.user && user2.user) {
            if (user1.user.lastName > user2.user.lastName) {
                return 1;
            } else if (user1.user.lastName < user2.user.lastName) {
                return -1;
            }
            if (user1.user.firstName > user2.user.firstName) {
                return 1;
            } else if (user1.user.firstName < user2.user.firstName) {
                return -1;
            }
        }
        return 0;
    }

    handleDatepickerOnChange = (newDate: moment.Moment) => {
        this.setState({ inviteExpiration: newDate });
    }

    hideBatchModal = () => {
        this.setState({showModal: false});
    };

    public render() {
        const { selectedOrganizationId, teamMemberTypeahead, teamAssociateMemberTypeahead, confirmedTeamMembers, confirmedAssociateTeamMembers, formErrors } = this.state;
        const organizationOptions = !this.props.organizations ? [] : this.props.organizations.map((organization) => {
            return {
                label: organization.name,
                value: organization.id
            };
        });
        const style = { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

        return (
            <Form
                defaultValues={this.props.defaultValues}
                onSubmit={submittedValues => this.handleFormSubmit(submittedValues)}
                formDidUpdate={this.formDidUpdate}
                validateError={this.validateErrors}
                validateSuccess={this.validateSuccess}
            >
                {formApi => (
                    <form onSubmit={formApi.submitForm} id="entityForm">
                        <Prompt
                            when={this.state.saveNeeded}
                            message='There are unsaved changes. Are you sure you want to leave?'
                        />
                        {this.state.isLoading && <LoadingIndicator></LoadingIndicator>}
                        <div className="form-group">
                            <label htmlFor="name">Name *</label>
                            <Text className="form-control" field="name" id="name"/>
                            {formErrors.name && <span className="help-block error-text">{formErrors.name}</span>}
                        </div>
                        {this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]) &&
                            <div className="form-group">
                                <label htmlFor="organizationId">Organization *</label>
                                <Select className="form-control" field="organizationId" id="organizationId"
                                        options={organizationOptions}/>
                                {formErrors.organizationId &&
                                    <span className="help-block error-text">{formErrors.organizationId}</span>}
                            </div>}
                        {selectedOrganizationId &&
                            <div>
                                <div className="form-group">
                                    <label>Team Members</label>
                                    <InputGroup>
                                        <AsyncTypeahead
                                            ref={(typeahead: any) => this.teamMemberTypeahead = typeahead}
                                            {...teamMemberTypeahead}
                                            minLength={2}
                                            onSearch={this.handleSearch}
                                            onChange={this.typeaheadOnChange}
                                            placeholder="Search for a user"
                                            labelKey={(option) => `${option.lastName}, ${option.firstName}`}
                                        />
                                        <InputGroup.Button>
                                            <button onClick={this.addUser} className="btn btn-default btn-add" aria-label="Add" title="Add">+</button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                                {(confirmedTeamMembers && confirmedTeamMembers.length > 0) &&
                                    <table className="table table-striped">
                                        <thead>
                                            <tr> 
                                                <th>Name</th>
                                                <th>Is Member?</th>
                                                <th>Is Manager?</th>
                                                <th className="row-header">
                                                    <button className="btn btn-xs btn-default" style={{ marginLeft: '5px' }} onClick={(e) => this.toggleAllFoundations(e)}>Foundations?</button>
                                                </th>
                                                {this.canBatch && <th>Invite sent?</th>}
                                                {this.canBatch && <th>Survey completed?</th>}
                                                {this.canBatch && <th className="row-header">
                                                    <button className="btn btn-xs btn-default" onClick={(e) => this.toggleAllForBatch(e)}>Include in Batch</button>
                                                </th>}
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {confirmedTeamMembers.sort(this.userTeamFilter).map((userteam, index) => {
                                            const userId = userteam.userID!;
                                            const isBatchable = this.isBatchable(userteam);
                                            const fullUser = this.state.fullUsersById.get(userId);
                                            const userLoadFailed = this.state.failedUserIds.indexOf(userId) >= 0;
                                            return (
                                                <tr key={`user-${userId}`}>
                                                    <td>
                                                        {this.getUserName(userId)}
                                                    </td>
                                                    <td>
                                                        <input type="checkbox" checked={!!userteam.isMember}
                                                               onChange={this.handleIsMemberChangeBinder(userId)}/>
                                                    </td>
                                                    <td>
                                                        <input type="checkbox" checked={!!userteam.isManager}
                                                               onChange={this.handleIsManagerChangeBinder(userId)}/>
                                                    </td>
                                                    <td>
                                                        {fullUser ? (
                                                            <input type="checkbox" checked={hasPermission(fullUser.userPermissions, UserPermissions.Foundations)}
                                                                   onChange={() => this.handleFoundationsChange(userId)}/>
                                                        ) : userLoadFailed ? (
                                                            <span title="Could not load this user">&mdash;</span>
                                                        ) : (
                                                            <span className="glyphicon glyphicon-refresh glyphicon-spin"></span>
                                                        )}
                                                    </td>
                                                    {this.canBatch && <td>
                                                        {isBatchable && <span>
                                                            {(this.state.inviteSentByUserId[userId] === undefined || this.state.inviteSentByUserId[userId] === null) &&
                                                                <span
                                                                    className="glyphicon glyphicon-refresh glyphicon-spin"></span>} {this.state.inviteSentByUserId[userId] &&
                                                            <span className="glyphicon glyphicon-ok"></span>}
                                                        </span>}
                                                    </td>}
                                                    {this.canBatch && <td>
                                                        {isBatchable && <span>
                                                            {(this.state.surveyCompletedByUserId[userId] === undefined || this.state.surveyCompletedByUserId[userId] === null) &&
                                                                <span
                                                                    className="glyphicon glyphicon-refresh glyphicon-spin"></span>} {this.state.surveyCompletedByUserId[userId] === false &&
                                                            <span></span>} {this.state.surveyCompletedByUserId[userId] &&
                                                            <span>{(new Date(this.state.surveyCompletedByUserId[userId].completedDate)).toLocaleDateString('en-US')}</span>}
                                                        </span>}
                                                    </td>}
                                                    {this.canBatch && <td>
                                                        {isBatchable &&
                                                            <input type="checkbox"
                                                                   checked={this.state.selectedUserIdsForBatch.includes(userId)}
                                                                   onChange={() => this.handleSelectUser(userId)}/>}
                                                    </td>}
                                                    <td>
                                                        <span onClick={this.removeUserBinder(userId)}
                                                              className="glyphicon glyphicon-trash"/>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {this.canBatch &&
                                            <tr className="batch-control-container">
                                                <td className="batch-control-title">
                                                    Batch actions
                                                </td>
                                                <td/>
                                                <td/>
                                                <td/>
                                                <td/>
                                                <td>
                                                    <span>Expiration</span>
                                                    <div className="invite-expiration-picker">
                                                        <Datepicker
                                                            popperPlacement={"top"}
                                                            selected={this.state.inviteExpiration}
                                                            className="invite-expiration-picker"
                                                            onChange={this.handleDatepickerOnChange}
                                                            dropdownMode="select"
                                                            maxDate={moment().add(1, 'y')}
                                                            minDate={moment()}
                                                        />
                                                    </div>
                                                </td>
                                                <td/>
                                            </tr>
                                        }
                                        {this.canBatch &&
                                            <tr className="batch-control-container">
                                                <td/>
                                                <td/>
                                                <td>
                                                    <button
                                                        className="btn btn-primary batch-button"
                                                        disabled={this.state.isLoading}
                                                        onClick={(e) => this.inviteBatch(e)}
                                                    >Invite batch
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-primary batch-button"
                                                        disabled={this.state.isLoading}
                                                        onClick={(e) => this.createLoginsForBatch(e)}
                                                    >Create logins
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-primary batch-button"
                                                        disabled={this.state.isLoading}
                                                        onClick={(e) => this.openBatchModal(e)}
                                                    >PDF batch
                                                    </button>
                                                </td>
                                                <td/>
                                                <td/>
                                            </tr>
                                        }
                                        </tbody>
                                    </table>
                                }
                            </div>
                        }

                        {selectedOrganizationId && this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]) &&
                            <div>
                                <div className="form-group">
                                    <label>Associate Members</label>
                                    <InputGroup>
                                        <AsyncTypeahead
                                            ref={(typeahead: any) => this.teamAssociateMemberTypeahead = typeahead}
                                            {...teamAssociateMemberTypeahead}
                                            minLength={2}
                                            onSearch={this.handleAssociateSearch}
                                            onChange={this.typeaheadAssociateOnChange}
                                            placeholder="Search for a user"
                                            labelKey={(option) => `${option.lastName}, ${option.firstName}`}
                                        />
                                        <InputGroup.Button>
                                            <button onClick={this.addAssociateUser} className="btn btn-default btn-add" aria-label="Add" title="Add">+
                                            </button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                                {(confirmedAssociateTeamMembers && confirmedAssociateTeamMembers.length > 0) &&
                                    <table className="table table-striped">
                                        <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th/>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {confirmedAssociateTeamMembers.map((userteam, index) => (
                                            <tr key={`user-${userteam.userID}`}>
                                                <td>
                                                    {userteam.user!.lastName}, {userteam.user!.firstName}
                                                </td>
                                                <td>
                                                    <span onClick={this.removeAssociateUserBinder(userteam.user!.id)}
                                                          className="glyphicon glyphicon-trash"/>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                }
                            </div>
                        }

                        <button type="submit" className="btn btn-primary" disabled={!this.state.canSubmit}>Save</button>
                        { this.state.showModal && <div className="modal-container">
                            <div className="row">
                                <div className="col-xs-offset-1 col-xs-10 col-sm-offset-2 col-sm-8">
                                    <div className="modal-dialog">
                                        <div className="modal-content" role="document">
                                            <div id='modal-thing'
                                                 className="modal-header pva-group-title pondera-modal-header">
                                                <button type="button" className="close pondera-close"
                                                        onClick={(e) => this.hideBatchModal()}>
                                                    <span className="glyphicon glyphicon-remove"/>
                                                    <span className="sr-only">Close</span>
                                                </button>
                                                <div className="modal-title">
                                                    <div className="pondera-modal-title batch-modal-title">Batch export</div>
                                                </div>
                                            </div>
                                            <div className="modal-body">
                                                {this.state.isLoading && <div className={"centered-spinner"}><LoadingIndicator></LoadingIndicator></div>}
                                                <table className="table table-striped">
                                                    <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        {<th>Report type</th>}
                                                        {<th>Include types?</th>}
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {this.state.selectedUserIdsForBatch.map((userId) => {
                                                        const typeDropdownId = `reportTypeFor_${userId}`;
                                                        const fullOption = {label:'Full',value:'full'};
                                                        const userteam = this.getUserTeamForUserId(userId);
                                                        if (userteam === null) {
                                                            return;
                                                        }
                                                        const briefOption = {label:'Brief',value:'brief'};
                                                        const foundationsOption = {label:'Foundations',value:'foundations'};
                                                            return (
                                                                <tr key={`user-${userId}`}>
                                                                    <td>
                                                                        {this.getUserName(userId)}
                                                                    </td>
                                                                    <td>
                                                                        <div className="form-group">
                                                                            <select className="form-control" id={typeDropdownId} value={this.userIsAssignedBriefReport(userteam.userID!) ? briefOption.value : this.userIsAssignedFoundationsReport(userteam.userID!) ? foundationsOption.value : fullOption.value} onChange={(e) => {this.reportTypeDropdownChanged(e, userteam.userID!)}}>
                                                                                <option key={userteam.userID + '_0'} value={fullOption.value}>{fullOption.label}</option>
                                                                                <option key={userteam.userID + '_1'} value={briefOption.value}>{briefOption.label}</option>
                                                                                <option key={userteam.userID + '_2'} value={foundationsOption.value}>{foundationsOption.label}</option>
                                                                            </select>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <input type="checkbox"
                                                                               checked={this.state.selectedUserIdsForTypesReport.includes(userteam.userID!)}
                                                                               onChange={() => this.handleToggleTypesReport(userteam.userID!)}/>
                                                                    </td>
                                                                </tr>
                                                        );
                                                        }
                                                    )}
                                                        <tr>
                                                            <td></td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-primary batch-button"
                                                                    disabled={this.state.isLoading}
                                                                    onClick={(e) => this.toggleAllForExportReportType(e)}
                                                                >Switch report type
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-primary batch-button"
                                                                    disabled={this.state.isLoading}
                                                                    onClick={(e) => this.toggleAllForPersonalityTypes(e)}
                                                                >Toggle include types
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="modal-footer">
                                                <button onClick={(e) => { this.exportBatch(e); }} type="button"
                                                        className="btn btn-primary modal-send"
                                                        disabled={this.state.isLoading}>Send
                                                </button>
                                                <button onClick={(e) => this.hideBatchModal()} type="button"
                                                        className="btn btn-default modal-cancel">Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </form>
                )}
            </Form>
        );
    }
    }


// WEBPACK FOOTER //
// ./src/components/team/TeamForm.tsx